// What every visitor is about to be told: whether the service is closed for
// maintenance, and the notice above the page if one is set.
//
// The shapes and the two decisions made from them live here rather than beside
// the fetch, because the layout and its components read them in the BROWSER as
// well as on the server, and nothing under `$lib/server` may be imported into a
// component. Fetching the answer is the server's half (./server/notice.ts).

export type MaintenanceMode = "off" | "read_only" | "full";

export type NoticeLevel = "info" | "warning" | "critical";

export interface Announcement {
  readonly text: string;
  readonly level: NoticeLevel;
  readonly until: string | null;
}

export interface ServiceNotice {
  readonly mode: MaintenanceMode;
  readonly announcement: Announcement | null;
}

export const OPEN_NOTICE: ServiceNotice = { mode: "off", announcement: null };

const MODES: readonly MaintenanceMode[] = ["off", "read_only", "full"];
const LEVELS: readonly NoticeLevel[] = ["info", "warning", "critical"];

// Reads the answer defensively. It arrives over the network, and the one thing
// this must never do is put something on the page that is not a plain string
// from a field this app named.
export function readNotice(body: unknown): ServiceNotice {
  if (typeof body !== "object" || body === null) return OPEN_NOTICE;
  const record = body as { mode?: unknown; announcement?: unknown };
  const mode = MODES.includes(record.mode as MaintenanceMode)
    ? (record.mode as MaintenanceMode)
    : "off";

  const raw = record.announcement;
  if (typeof raw !== "object" || raw === null) return { mode, announcement: null };
  const notice = raw as { text?: unknown; level?: unknown; until?: unknown };
  if (typeof notice.text !== "string" || notice.text.trim() === "") {
    return { mode, announcement: null };
  }
  return {
    mode,
    announcement: {
      text: notice.text,
      level: LEVELS.includes(notice.level as NoticeLevel) ? (notice.level as NoticeLevel) : "info",
      until: typeof notice.until === "string" ? notice.until : null,
    },
  };
}

// Addresses a closure never replaces.
//
// The console, because an operator has to be able to reach the switch that
// turns this off. And the status page, because while the service is closed it
// is precisely the page a visitor needs: replacing the one address that says
// how long this lasts with a page that says "down for maintenance" answers a
// question nobody asked with the thing they already know.
const EXEMPT_PREFIXES: readonly string[] = ["/admin", "/status"];

function exempt(pathname: string): boolean {
  return EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// Whether this address should be replaced by the maintenance page rather than
// carrying a banner above it.
//
// Only `full` replaces a page: under `read_only` the site still reads, and a
// person who can read their history and their balance is better served by a
// notice above them than by a wall.
export function maintenancePageFor(mode: MaintenanceMode, pathname: string): boolean {
  if (mode !== "full") return false;
  return !exempt(pathname);
}

// Whether a read-only notice belongs above this address. The console is exempt
// for the reason given above; the status page is not, because a bar saying the
// service is read-only is exactly what that page is there to carry.
export function readOnlyNoticeFor(mode: MaintenanceMode, pathname: string): boolean {
  if (mode !== "read_only") return false;
  return !pathname.startsWith("/admin");
}
